import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowRight,
  ArrowLeft,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Target,
  Wallet,
  CreditCard,
  PiggyBank,
  Calendar,
  CheckCircle,
  AlertTriangle,
  Lightbulb,
  RefreshCw,
  ChevronLeft,
  Info,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const NetWorthCalculator = ({ onBack }) => {
  const [step, setStep] = useState(1); // 1: Assets, 2: Liabilities, 3: Goal, 4: Behavior, 5: Results
  const [isCalculating, setIsCalculating] = useState(false);
  const [results, setResults] = useState(null);
  
  // Assets state
  const [assets, setAssets] = useState({
    chequing_savings: "",
    investments: "",
    cash: "",
    security_deposits: "",
    other_valuables: "",
  });
  
  // Liabilities state
  const [liabilities, setLiabilities] = useState({
    student_loans: "",
    credit_cards_loc: "",
    bnpl_phone_plans: "",
    car_loan_lease: "",
    other_debts: "",
  });
  
  // Goal state
  const [goal, setGoal] = useState({
    target_net_worth: "",
    target_date: "",
  });
  
  // Behavior state
  const [behavior, setBehavior] = useState({
    current_savings_per_month: "",
    willing_to_work_more: "",
    spending_flexibility: "",
    primary_priority: "",
  });
  
  const assetLabels = {
    chequing_savings: "Chequing & Savings Accounts",
    investments: "Investments (ETFs, stocks, robo-advisors)",
    cash: "Cash on Hand",
    security_deposits: "Security Deposits (rent deposit, etc.)",
    other_valuables: "Other Valuables (laptop, bike, etc.)",
  };
  
  const liabilityLabels = {
    student_loans: "Student Loans",
    credit_cards_loc: "Credit Cards / Lines of Credit",
    bnpl_phone_plans: "Buy-Now-Pay-Later / Phone Plans",
    car_loan_lease: "Car Loan / Lease",
    other_debts: "Other Debts (friends, family, unpaid bills)",
  };
  
  const parseAmount = (value) => {
    const num = parseFloat(value.replace(/[^0-9.-]/g, ""));
    return isNaN(num) ? 0 : num;
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };
  
  const calculateNetWorth = () => {
    const totalAssets = Object.values(assets).reduce((sum, val) => sum + parseAmount(val), 0);
    const totalLiabilities = Object.values(liabilities).reduce((sum, val) => sum + parseAmount(val), 0);
    return totalAssets - totalLiabilities;
  };
  
  const getTotalAssets = () => {
    return Object.values(assets).reduce((sum, val) => sum + parseAmount(val), 0);
  };
  
  const getTotalLiabilities = () => {
    return Object.values(liabilities).reduce((sum, val) => sum + parseAmount(val), 0);
  };
  
  const calculateResults = () => {
    setIsCalculating(true);
    
    const totalAssets = getTotalAssets();
    const totalLiabilities = getTotalLiabilities();
    const currentNetWorth = totalAssets - totalLiabilities;
    const targetNetWorth = parseAmount(goal.target_net_worth);
    
    // Calculate months until goal
    const today = new Date();
    const targetDate = new Date(goal.target_date);
    const monthsUntilGoal = Math.max(1, Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24 * 30)));
    
    const netWorthGap = targetNetWorth - currentNetWorth;
    const requiredMonthlyChange = netWorthGap / monthsUntilGoal;
    const currentMonthlySavings = parseAmount(behavior.current_savings_per_month);
    
    // Goal realism calculation
    let goalRealism = "realistic";
    let goalRealismExplanation = "";
    
    const savingsRatio = currentMonthlySavings / requiredMonthlyChange;
    
    if (requiredMonthlyChange <= 0) {
      goalRealism = "realistic";
      goalRealismExplanation = "You've already reached or exceeded your goal! Consider setting a new target.";
    } else if (savingsRatio >= 0.8) {
      goalRealism = "realistic";
      goalRealismExplanation = "Your current savings rate is close to or exceeds what's needed. Keep it up!";
    } else if (savingsRatio >= 0.5 || behavior.spending_flexibility === "can_cut_a_lot" || behavior.willing_to_work_more === "plus_10_hours_week") {
      goalRealism = "challenging";
      goalRealismExplanation = "This goal is achievable but will require some adjustments to your current habits.";
    } else {
      goalRealism = "unrealistic";
      goalRealismExplanation = "This goal may be too aggressive for your current situation. Consider extending your timeline or adjusting your target.";
    }
    
    // Generate suggestions based on priority
    const suggestions = generateSuggestions(
      behavior.primary_priority,
      totalLiabilities,
      liabilities,
      currentMonthlySavings,
      behavior.spending_flexibility,
      behavior.willing_to_work_more
    );
    
    // Get top asset and liability categories
    const topAssets = Object.entries(assets)
      .map(([key, val]) => ({ category: key, label: assetLabels[key], amount: parseAmount(val) }))
      .filter(a => a.amount > 0)
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 3);
    
    const topLiabilities = Object.entries(liabilities)
      .map(([key, val]) => ({ category: key, label: liabilityLabels[key], amount: parseAmount(val) }))
      .filter(l => l.amount > 0)
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 3);
    
    const result = {
      summary: {
        current_net_worth: currentNetWorth,
        total_assets: totalAssets,
        total_liabilities: totalLiabilities,
        target_net_worth: targetNetWorth,
        target_date: goal.target_date,
        net_worth_gap: netWorthGap,
        months_until_goal: monthsUntilGoal,
        required_monthly_change: requiredMonthlyChange,
        current_monthly_savings: currentMonthlySavings,
        goal_realism: goalRealism,
        goal_realism_explanation: goalRealismExplanation,
      },
      breakdown: {
        top_assets: topAssets,
        top_liabilities: topLiabilities,
      },
      suggestions: suggestions,
    };
    
    setTimeout(() => {
      setResults(result);
      setIsCalculating(false);
      setStep(5);
    }, 1000);
  };
  
  const generateSuggestions = (priority, totalLiabilities, liabilitiesObj, monthlySavings, spendingFlex, workMore) => {
    const suggestions = [];
    
    const hasHighInterestDebt = parseAmount(liabilitiesObj.credit_cards_loc) > 0 || parseAmount(liabilitiesObj.bnpl_phone_plans) > 0;
    const hasStudentLoans = parseAmount(liabilitiesObj.student_loans) > 0;
    
    // Priority-based suggestions
    if (priority === "pay_off_high_interest_debt" || hasHighInterestDebt) {
      if (hasHighInterestDebt) {
        suggestions.push({
          id: "pay_cc_debt",
          title: "Tackle High-Interest Debt First",
          description: "Credit cards and buy-now-pay-later plans often charge 20%+ interest. Even small extra payments can save you hundreds in interest over time.",
          impact: "Could save you $500-2000+ in interest annually depending on your balance.",
          timeframe: "short_term",
          priority: priority === "pay_off_high_interest_debt",
        });
      }
    }
    
    if (priority === "build_emergency_fund" || monthlySavings < 500) {
      suggestions.push({
        id: "emergency_fund",
        title: "Build a Starter Emergency Fund",
        description: "Aim for $500-$1,500 in a savings account for unexpected expenses. This prevents you from going into debt when life happens.",
        impact: "Provides financial security and prevents credit card debt from emergencies.",
        timeframe: "short_term",
        priority: priority === "build_emergency_fund",
      });
    }
    
    if (priority === "start_investing") {
      suggestions.push({
        id: "start_investing",
        title: "Start Small with Investing",
        description: "Once you have an emergency fund and high-interest debt is under control, consider starting with $25-50/month in a diversified ETF through a TFSA.",
        impact: "Even small amounts grow significantly over 10+ years due to compound growth.",
        timeframe: "medium_term",
        priority: true,
      });
    }
    
    // Behavioral suggestions based on flexibility
    if (spendingFlex === "can_cut_a_lot") {
      suggestions.push({
        id: "reduce_subscriptions",
        title: "Review Your Subscriptions",
        description: "Go through your bank statements and cancel any subscriptions you don't use regularly. Even $30/month in unused subscriptions is $360/year.",
        impact: "Could free up $200-500+ annually with minimal lifestyle impact.",
        timeframe: "short_term",
        priority: false,
      });
    }
    
    if (workMore === "plus_5_hours_week" || workMore === "plus_10_hours_week") {
      suggestions.push({
        id: "increase_income",
        title: "Boost Your Income",
        description: `Working ${workMore === "plus_10_hours_week" ? "10" : "5"} extra hours per week at $15-20/hr could add $${workMore === "plus_10_hours_week" ? "600-800" : "300-400"}/month to your income.`,
        impact: `Potential to add $${workMore === "plus_10_hours_week" ? "7,200-9,600" : "3,600-4,800"}/year to your net worth.`,
        timeframe: "short_term",
        priority: false,
      });
    }
    
    // Always include automation tip
    suggestions.push({
      id: "automate_savings",
      title: "Automate Your Savings",
      description: "Set up automatic transfers on payday. Move money to savings before you can spend it. Even $25/week adds up to $1,300/year.",
      impact: "Makes saving effortless and consistent.",
      timeframe: "short_term",
      priority: false,
    });
    
    // Student loan specific advice
    if (hasStudentLoans) {
      suggestions.push({
        id: "student_loan_strategy",
        title: "Optimize Student Loan Repayment",
        description: "If you have government student loans, interest doesn't accrue while you're in school. Focus on high-interest debt first, then tackle student loans after graduation.",
        impact: "Prevents paying unnecessary interest on other debts.",
        timeframe: "medium_term",
        priority: false,
      });
    }
    
    // Sort by priority
    return suggestions.sort((a, b) => (b.priority ? 1 : 0) - (a.priority ? 1 : 0)).slice(0, 4);
  };
  
  const handleRestart = () => {
    setStep(1);
    setResults(null);
    setAssets({
      chequing_savings: "",
      investments: "",
      cash: "",
      security_deposits: "",
      other_valuables: "",
    });
    setLiabilities({
      student_loans: "",
      credit_cards_loc: "",
      bnpl_phone_plans: "",
      car_loan_lease: "",
      other_debts: "",
    });
    setGoal({
      target_net_worth: "",
      target_date: "",
    });
    setBehavior({
      current_savings_per_month: "",
      willing_to_work_more: "",
      spending_flexibility: "",
      primary_priority: "",
    });
  };
  
  const handleDownloadPDF = async () => {
    const element = document.getElementById("networth-results");
    if (!element) return;
    
    try {
      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
      pdf.save("my-net-worth-report.pdf");
      toast.success("Report downloaded!");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };
  
  const currentNetWorth = calculateNetWorth();
  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;
  
  // Step 1: Assets
  if (step === 1) {
    return (
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => window.location.href = "/tools"}
          className="mb-4 text-slate-600 hover:text-navy-700"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to All Tools
        </Button>
        
        <Card className="border-0 shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Wallet className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-xl font-heading text-navy-700">
                  Step 1: Your Assets
                </CardTitle>
                <p className="text-sm text-slate-500">What do you own?</p>
              </div>
            </div>
            <Progress value={25} className="h-2" />
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600 mb-4">
              Enter approximate values. It&apos;s okay to estimate - we&apos;re looking for a general picture, not exact numbers.
            </p>
            
            {Object.entries(assetLabels).map(([key, label]) => (
              <div key={key} className="space-y-1">
                <Label htmlFor={key} className="text-sm text-slate-700">{label}</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id={key}
                    type="text"
                    placeholder="0"
                    value={assets[key]}
                    onChange={(e) => setAssets({ ...assets, [key]: e.target.value })}
                    className="pl-9"
                  />
                </div>
              </div>
            ))}
            
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between mb-4">
                <span className="font-medium text-slate-700">Total Assets:</span>
                <span className="text-xl font-bold text-emerald-600">{formatCurrency(getTotalAssets())}</span>
              </div>
              <Button
                onClick={() => setStep(2)}
                className="w-full bg-emerald-500 hover:bg-emerald-600"
              >
                Next: Your Debts
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Step 2: Liabilities
  if (step === 2) {
    return (
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => window.location.href = "/tools"}
          className="mb-4 text-slate-600 hover:text-navy-700"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to All Tools
        </Button>
        
        <Card className="border-0 shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <CardTitle className="text-xl font-heading text-navy-700">
                  Step 2: Your Debts
                </CardTitle>
                <p className="text-sm text-slate-500">What do you owe?</p>
              </div>
            </div>
            <Progress value={50} className="h-2" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-700">
                <Info className="w-4 h-4 inline mr-1" />
                Don&apos;t worry if you have debt - it&apos;s completely normal for students! This helps us give you better advice.
              </p>
            </div>
            
            {Object.entries(liabilityLabels).map(([key, label]) => (
              <div key={key} className="space-y-1">
                <Label htmlFor={key} className="text-sm text-slate-700">{label}</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id={key}
                    type="text"
                    placeholder="0"
                    value={liabilities[key]}
                    onChange={(e) => setLiabilities({ ...liabilities, [key]: e.target.value })}
                    className="pl-9"
                  />
                </div>
              </div>
            ))}
            
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-slate-700">Total Debts:</span>
                <span className="text-xl font-bold text-red-500">{formatCurrency(getTotalLiabilities())}</span>
              </div>
              <div className="flex items-center justify-between mb-4 p-3 bg-slate-50 rounded-lg">
                <span className="font-medium text-slate-700">Current Net Worth:</span>
                <span className={`text-xl font-bold ${currentNetWorth >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                  {formatCurrency(currentNetWorth)}
                </span>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                >
                  Next: Your Goal
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Step 3: Goal
  if (step === 3) {
    return (
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => window.location.href = "/tools"}
          className="mb-4 text-slate-600 hover:text-navy-700"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to All Tools
        </Button>
        
        <Card className="border-0 shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-xl font-heading text-navy-700">
                  Step 3: Your Goal
                </CardTitle>
                <p className="text-sm text-slate-500">Where do you want to be?</p>
              </div>
            </div>
            <Progress value={75} className="h-2" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-slate-600 mb-2">
                <strong>Your current net worth:</strong>{" "}
                <span className={currentNetWorth >= 0 ? "text-emerald-600" : "text-red-500"}>
                  {formatCurrency(currentNetWorth)}
                </span>
              </p>
              <p className="text-xs text-slate-500">
                Setting a goal helps you track progress. Be realistic but ambitious!
              </p>
            </div>
            
            <div className="space-y-1">
              <Label htmlFor="target_net_worth" className="text-sm text-slate-700">
                Target Net Worth
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="target_net_worth"
                  type="text"
                  placeholder="e.g., 5000"
                  value={goal.target_net_worth}
                  onChange={(e) => setGoal({ ...goal, target_net_worth: e.target.value })}
                  className="pl-9"
                />
              </div>
            </div>
            
            <div className="space-y-1">
              <Label htmlFor="target_date" className="text-sm text-slate-700">
                Target Date
              </Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="target_date"
                  type="date"
                  value={goal.target_date}
                  onChange={(e) => setGoal({ ...goal, target_date: e.target.value })}
                  className="pl-9"
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>
            </div>
            
            <div className="pt-4 border-t">
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={() => setStep(4)}
                  disabled={!goal.target_net_worth || !goal.target_date}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                >
                  Next: Your Habits
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Step 4: Behavior
  if (step === 4) {
    return (
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => window.location.href = "/tools"}
          className="mb-4 text-slate-600 hover:text-navy-700"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to All Tools
        </Button>
        
        <Card className="border-0 shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-xl font-heading text-navy-700">
                  Step 4: Your Habits
                </CardTitle>
                <p className="text-sm text-slate-500">Help us personalize your plan</p>
              </div>
            </div>
            <Progress value={100} className="h-2" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="monthly_savings" className="text-sm text-slate-700">
                How much can you save per month?
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="monthly_savings"
                  type="text"
                  placeholder="e.g., 200"
                  value={behavior.current_savings_per_month}
                  onChange={(e) => setBehavior({ ...behavior, current_savings_per_month: e.target.value })}
                  className="pl-9"
                />
              </div>
            </div>
            
            <div className="space-y-1">
              <Label className="text-sm text-slate-700">
                Are you willing to work more hours?
              </Label>
              <Select
                value={behavior.willing_to_work_more}
                onValueChange={(value) => setBehavior({ ...behavior, willing_to_work_more: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No, I can&apos;t work more</SelectItem>
                  <SelectItem value="plus_5_hours_week">Yes, about 5 hours/week</SelectItem>
                  <SelectItem value="plus_10_hours_week">Yes, about 10 hours/week</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1">
              <Label className="text-sm text-slate-700">
                How flexible is your spending?
              </Label>
              <Select
                value={behavior.spending_flexibility}
                onValueChange={(value) => setBehavior({ ...behavior, spending_flexibility: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="very_tight">Very tight - no room to cut</SelectItem>
                  <SelectItem value="can_cut_a_bit">Can cut a bit here and there</SelectItem>
                  <SelectItem value="can_cut_a_lot">Can cut a lot if needed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1">
              <Label className="text-sm text-slate-700">
                What&apos;s your #1 priority right now?
              </Label>
              <Select
                value={behavior.primary_priority}
                onValueChange={(value) => setBehavior({ ...behavior, primary_priority: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select your priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pay_off_high_interest_debt">Pay off high-interest debt</SelectItem>
                  <SelectItem value="build_emergency_fund">Build an emergency fund</SelectItem>
                  <SelectItem value="start_investing">Start investing</SelectItem>
                  <SelectItem value="grow_net_worth_general">Grow my net worth overall</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="pt-4 border-t">
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(3)}
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={calculateResults}
                  disabled={!behavior.current_savings_per_month || !behavior.willing_to_work_more || !behavior.spending_flexibility || !behavior.primary_priority || isCalculating}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                >
                  {isCalculating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      Calculate My Plan
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Step 5: Results
  if (step === 5 && results) {
    const { summary, breakdown, suggestions } = results;
    
    return (
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => window.location.href = "/tools"}
          className="mb-4 text-slate-600 hover:text-navy-700"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to All Tools
        </Button>
        
        <div id="networth-results">
          {/* Summary Card */}
          <Card className="border-0 shadow-xl mb-6">
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <Badge className="bg-emerald-100 text-emerald-700 mb-3">
                  Your Net Worth Report
                </Badge>
                <h2 className="text-4xl font-heading font-bold mb-2">
                  <span className={summary.current_net_worth >= 0 ? "text-emerald-600" : "text-red-500"}>
                    {formatCurrency(summary.current_net_worth)}
                  </span>
                </h2>
                <p className="text-slate-500">Current Net Worth</p>
              </div>
              
              {summary.current_net_worth < 0 && (
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-700">
                    <Info className="w-4 h-4 inline mr-1" />
                    <strong>Negative net worth is completely normal for students!</strong> Most people start with debt from education. What matters is your plan to improve it over time.
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-emerald-50 rounded-lg p-4 text-center">
                  <TrendingUp className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-emerald-600">{formatCurrency(summary.total_assets)}</p>
                  <p className="text-sm text-slate-600">Total Assets</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <TrendingDown className="w-6 h-6 text-red-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-red-500">{formatCurrency(summary.total_liabilities)}</p>
                  <p className="text-sm text-slate-600">Total Debts</p>
                </div>
              </div>
              
              {/* Goal Progress */}
              <div className="border-t pt-6">
                <h3 className="font-heading font-bold text-lg text-navy-700 mb-4">Your Goal Progress</h3>
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-600">Current</span>
                    <span className="text-sm text-slate-600">Target: {formatCurrency(summary.target_net_worth)}</span>
                  </div>
                  <Progress 
                    value={Math.max(0, Math.min(100, (summary.current_net_worth / summary.target_net_worth) * 100))} 
                    className="h-3 mb-2" 
                  />
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">{formatCurrency(summary.net_worth_gap)} to go</span>
                    <span className="text-slate-500">{summary.months_until_goal} months left</span>
                  </div>
                </div>
                
                <div className={`rounded-lg p-4 mb-4 ${
                  summary.goal_realism === "realistic" ? "bg-emerald-50 border-l-4 border-l-emerald-500" :
                  summary.goal_realism === "challenging" ? "bg-amber-50 border-l-4 border-l-amber-500" :
                  "bg-red-50 border-l-4 border-l-red-500"
                }`}>
                  <div className="flex items-start gap-3">
                    {summary.goal_realism === "realistic" ? (
                      <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                    ) : summary.goal_realism === "challenging" ? (
                      <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                    )}
                    <div>
                      <p className={`font-medium mb-1 ${
                        summary.goal_realism === "realistic" ? "text-emerald-700" :
                        summary.goal_realism === "challenging" ? "text-amber-700" :
                        "text-red-700"
                      }`}>
                        {summary.goal_realism === "realistic" ? "Your goal is realistic!" :
                         summary.goal_realism === "challenging" ? "Your goal is challenging but possible" :
                         "Your goal may need adjusting"}
                      </p>
                      <p className="text-sm text-slate-600">{summary.goal_realism_explanation}</p>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-navy-700">{formatCurrency(summary.required_monthly_change)}</p>
                    <p className="text-xs text-slate-500">Monthly improvement needed</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-navy-700">{formatCurrency(summary.current_monthly_savings)}</p>
                    <p className="text-xs text-slate-500">Your current monthly savings</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Breakdown Card */}
          {(breakdown.top_assets.length > 0 || breakdown.top_liabilities.length > 0) && (
            <Card className="border-0 shadow-xl mb-6">
              <CardContent className="p-6">
                <h3 className="font-heading font-bold text-lg text-navy-700 mb-4">Your Breakdown</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {breakdown.top_assets.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-emerald-600 mb-3">Top Assets</p>
                      <div className="space-y-2">
                        {breakdown.top_assets.map((asset, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-emerald-50 rounded-lg">
                            <span className="text-sm text-slate-700">{asset.label}</span>
                            <span className="font-medium text-emerald-600">{formatCurrency(asset.amount)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {breakdown.top_liabilities.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-red-500 mb-3">Top Debts</p>
                      <div className="space-y-2">
                        {breakdown.top_liabilities.map((liability, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                            <span className="text-sm text-slate-700">{liability.label}</span>
                            <span className="font-medium text-red-500">{formatCurrency(liability.amount)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Suggestions Card */}
          <Card className="border-0 shadow-xl mb-6">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                <h3 className="font-heading font-bold text-lg text-navy-700">Personalized Suggestions</h3>
              </div>
              
              <div className="space-y-4">
                {suggestions.map((suggestion, idx) => (
                  <div key={idx} className={`rounded-lg p-4 ${suggestion.priority ? "bg-emerald-50 border-l-4 border-l-emerald-500" : "bg-slate-50"}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${suggestion.priority ? "bg-emerald-500" : "bg-slate-300"}`}>
                        <span className="text-white text-xs font-bold">{idx + 1}</span>
                      </div>
                      <div>
                        <p className="font-medium text-navy-700 mb-1">{suggestion.title}</p>
                        <p className="text-sm text-slate-600 mb-2">{suggestion.description}</p>
                        <p className="text-xs text-emerald-600 font-medium">
                          Impact: {suggestion.impact}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          
          {/* Disclaimer */}
          <Card className="border-0 shadow-lg mb-6 bg-amber-50 border-l-4 border-l-amber-400">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-amber-800 mb-1">Important Note</p>
                  <p className="text-sm text-amber-700">
                    This is an educational tool, not financial advice. Your situation is unique - consider speaking with a financial advisor for personalized guidance.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Button onClick={handleDownloadPDF} className="flex-1 bg-emerald-500 hover:bg-emerald-600">
            <Download className="w-4 h-4 mr-2" />
            Download Report
          </Button>
          <Button onClick={handleRestart} variant="outline" className="flex-1">
            <RefreshCw className="w-4 h-4 mr-2" />
            Start Over
          </Button>
        </div>
      </div>
    );
  }
  
  return null;
};

export default NetWorthCalculator;
